import { SubscriptionType, UserRole } from '@whispers/sdk';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { getMembershipUrl } from '../utils/membership';

type NavItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
};

const navItems = ({ isInnerCircle }: { isInnerCircle: boolean }): NavItem[] => [
  {
    path: '/feeds',
    label: 'Feed',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  },
  {
    path: '/card-collection',
    label: 'Cards',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    ),
  },
  ...(isInnerCircle
    ? [
        {
          path: '/inner-circle/welcome',
          label: 'Inner Circle',
          icon: (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
              <circle cx="12" cy="12" r="4.5" strokeWidth={1.5} />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="m12 9.5.8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8.8-1.7Z"
              />
            </svg>
          ),
        },
      ]
    : []),
  {
    path: '/account',
    label: 'Account',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
  },
];

export const BottomNavigation = () => {
  const location = useLocation();
  const { user } = useAuth();

  const isInnerCircle =
    user?.subscriptionType === SubscriptionType.enum.INNER_CIRCLE ||
    [UserRole.enum.ADMIN, UserRole.enum.TESTER].some((role) => role === user?.role);

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface pb-safe">
      <div className="mx-auto flex max-w-prose items-center justify-around">
        {navItems({ isInnerCircle }).map((item) => {
          const isActive = location.pathname === item.path;
          const isDisabled = item.path === '/inner-circle/welcome' && !isInnerCircle;

          return (
            <Link
              key={item.path}
              to={
                isDisabled ? getMembershipUrl('inner_circle', '/inner-circle/welcome') : item.path
              }
              className={`flex flex-1 flex-col items-center py-2 transition-colors ${
                isActive
                  ? 'rounded-lg bg-button-solid-bg text-text-inverse hover:bg-button-solid-bg-hover disabled:bg-disabled-bg'
                  : 'text-text-secondary hover:text-text'
              } ${isDisabled ? 'opacity-50' : ''}`}
            >
              {item.icon}
              <span className="mt-1 text-xs">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
